/**
 * Локализация (MVP_TECH_RFC §2). Немецкий — основной язык интерфейса,
 * русский и английский — полноценные локализации.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import de from './locales/de.json';
import en from './locales/en.json';
import ru from './locales/ru.json';

export const SUPPORTED_LOCALES = ['de', 'ru', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Основной язык продукта: рынок Германия, договорный язык немецкий. */
export const DEFAULT_LOCALE: SupportedLocale = 'de';

/**
 * Привести язык интерфейса к поддерживаемой локали.
 *
 * Нужна, когда язык уходит **на сервер**: детектор возвращает и `ru-RU`, и
 * пустую строку до инициализации, а backend принимает только три значения и на
 * четвёртое отвечает `422`. Молчаливый отказ создать заказ хуже немецкого Karl.
 */
export function toSupportedLocale(value: string | undefined | null): SupportedLocale {
  const base = (value ?? '').toLowerCase().split('-')[0] ?? '';
  return (SUPPORTED_LOCALES as readonly string[]).includes(base)
    ? (base as SupportedLocale)
    : DEFAULT_LOCALE;
}

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  de: 'Deutsch',
  ru: 'Русский',
  en: 'English',
};

export const LOCALE_STORAGE_KEY = 'carviq.locale';

/**
 * Язык документа идёт за языком интерфейса (FE-572).
 *
 * `lang="de"` в `index.html` — только значение до старта приложения. Без этой
 * синхронизации скринридер читает русский и английский текст немецким
 * произношением, а браузер предлагает перевести страницу «с немецкого».
 * Значение сводится к поддерживаемой локали так же, как язык заказа: `ru-RU`
 * даёт `ru`, неизвестный язык — основной, на котором интерфейс и показан.
 */
function syncDocumentLang(language: string | undefined): void {
  document.documentElement.lang = toSupportedLocale(language);
}

export function initI18n() {
  if (i18n.isInitialized) return i18n;

  // Подписка — до `init`: первое переключение языка происходит внутри самой
  // инициализации (язык выбирает детектор), и его тоже нужно поймать.
  i18n.on('languageChanged', syncDocumentLang);

  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        de: { translation: de },
        ru: { translation: ru },
        en: { translation: en },
      },
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      nonExplicitSupportedLngs: true,
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      interpolation: { escapeValue: false },
    });

  return i18n;
}

export { i18n };
